"use client"
import { Grid, Card, CardContent, Typography, Box, Paper, List, ListItem, ListItemText, Chip } from "@mui/material"
import { People, CalendarToday, LocalPharmacy, Science, Warning } from "@mui/icons-material"
import { useApp } from "../contexts/AppContext"

export default function DashboardHome({ user }) {
  const { state } = useApp()

  const stats = [
    { title: "Total Patients", value: state.patients.length.toString(), icon: <People />, color: "#1976d2" },
    {
      title: "Today's Appointments",
      value: state.appointments.filter((apt) => apt.date === new Date().toISOString().split("T")[0]).length.toString(),
      icon: <CalendarToday />,
      color: "#388e3c",
    },
    {
      title: "Pending Lab Tests",
      value: state.labTests.filter((test) => test.status === "Pending").length.toString(),
      icon: <Science />,
      color: "#7b1fa2",
    },
    {
      title: "Low Stock Medicines",
      value: state.medicines.filter((med) => med.status === "Low Stock").length.toString(),
      icon: <LocalPharmacy />,
      color: "#f57c00",
    },
  ]

  const recentActivities = [
    {
      text: `New patient registered: ${state.patients[state.patients.length - 1]?.name || "N/A"}`,
      time: "10 minutes ago",
      type: "info",
    },
    {
      text: `Appointment scheduled with ${state.appointments[state.appointments.length - 1]?.doctorName || "N/A"}`,
      time: "25 minutes ago",
      type: "success",
    },
    {
      text: `Lab report ready for Patient ID: ${state.labTests.find((t) => t.status === "Completed")?.patientId || "N/A"}`,
      time: "1 hour ago",
      type: "warning",
    },
    { text: "Pharmacy order completed", time: "2 hours ago", type: "info" },
  ]

  const alerts = [
    {
      text: `${state.medicines.filter((m) => m.status === "Low Stock").length} medicines running low on stock`,
      type: "error",
    },
    {
      text: `${state.inventory.filter((i) => i.status === "Out of Stock").length} inventory items out of stock`,
      type: "warning",
    },
    { text: `${state.bills.filter((b) => b.status === "Pending").length} pending bills to collect`, type: "info" },
  ]

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Welcome back, {user.name}!
      </Typography>

      <Grid container spacing={3}>
        {/* Stats Cards */}
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      backgroundColor: stat.color,
                      color: "white",
                      borderRadius: "50%",
                      p: 1,
                      mr: 2,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h5">{stat.value}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Recent Activities */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activities
            </Typography>
            <List>
              {recentActivities.map((activity, index) => (
                <ListItem key={index} divider>
                  <ListItemText primary={activity.text} secondary={activity.time} />
                  <Chip
                    label={activity.type}
                    size="small"
                    color={
                      activity.type === "success" ? "success" : activity.type === "warning" ? "warning" : "primary"
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Alerts */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              <Warning sx={{ mr: 1, verticalAlign: "middle" }} />
              Alerts
            </Typography>
            <List>
              {alerts.map((alert, index) => (
                <ListItem key={index} divider>
                  <ListItemText primary={alert.text} />
                  <Chip
                    label={alert.type}
                    size="small"
                    color={alert.type === "error" ? "error" : alert.type === "warning" ? "warning" : "info"}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}
